import React, { useEffect } from 'react'

import * as d3 from 'd3'

import styles from './VersionChart.module.scss'

export interface VersionChartDatapoint {
  version: string
  count: number
}

interface VersionChartProps {
  height: number
  width?: number
  data: VersionChartDatapoint[]
}

const resize = (
  svg: d3.Selection<SVGSVGElement, unknown, HTMLElement, unknown>,
  container: d3.Selection<Element | d3.EnterElement | Document | Window, unknown, null, undefined>,
  aspect: number
) => {
  if (!svg || !container) {
    return
  }

  const targetWidth = parseInt(container.style('width'))

  svg.attr('width', targetWidth)
  svg.attr('height', Math.round(targetWidth / aspect))
}

const makeResponsive = (svg: d3.Selection<SVGSVGElement, unknown, HTMLElement, unknown>) => {
  const parent = svg.node()?.parentNode as d3.BaseType

  if (!parent) {
    return
  }

  const container = d3.select(parent)
  const width = parseInt(svg.style('width'))
  const height = parseInt(svg.style('height'))
  const aspect = width / height

  svg.attr('viewBox', `0 0 ${width} ${height}`).attr('perserveAspectRatio', 'xMinYMid')

  const doResize = () => {
    resize(svg, container, aspect)
  }

  window.addEventListener('resize', doResize)

  resize(svg, container, aspect)

  return () => {
    window.removeEventListener('resize', doResize)
  }
}

const VersionChart: React.FC<VersionChartProps> = ({ height, width = 1000, data }) => {
  const margin = 60
  const chartWidth = width - 2 * margin
  const chartHeight = height - 2 * margin

  useEffect(() => {
    const draw = () => {
      const container = d3.select('#versionChart')
      container.selectAll('*').remove()

      const svg = container.append('svg')

      const chart = svg.append('g').attr('transform', `translate(${margin}, ${margin})`)

      const max = d3.max(data.map((d) => d.count)) || 0
      const yScale = d3.scaleLinear().range([chartHeight, 0]).domain([0, max]).nice()

      const yAxisTicks = yScale.ticks().filter((tick) => Number.isInteger(tick))

      chart.append('g').call(d3.axisLeft(yScale).tickValues(yAxisTicks).tickFormat(d3.format('d')))

      const xScale = d3
        .scaleBand()
        .range([0, chartWidth])
        .domain(data.map((d) => d.version))
        .padding(0.2)

      chart.append('g').attr('transform', `translate(0, ${chartHeight})`).call(d3.axisBottom(xScale))

      chart
        .selectAll()
        .data(data)
        .enter()
        .append('rect')
        .attr('x', (s) => (xScale(s.version) || 0) + xScale.bandwidth() / 2 - Math.min(xScale.bandwidth(), 100) / 2)
        .attr('y', (s) => yScale(s.count))
        .attr('height', (s) => chartHeight - yScale(s.count))
        .attr('width', Math.min(xScale.bandwidth(), 100))

      svg.attr('width', width).attr('height', height)

      return makeResponsive(svg)
    }

    return draw()
  }, [data, chartHeight, chartWidth, height, width])

  return <div className={`mb-4 ${styles.chart}`} id="versionChart"></div>
}

export default VersionChart
